import { useTranslation } from 'react-i18next'
import ActionStatusTag from './ActionStatusTag'
import { formatDate } from '../../../lib/datetime'
import { deleteLabel } from '../../../lib/deleteLabel'
import type { SubAction } from '../types'

/**
 * Alt görevlerin dar ekran (≤`CARD_VIEW_BREAKPOINT`) karşılığı — masaüstündeki `DataTable`'ın yerine
 * (aynı anda değil) çizilir. Sayfa JSX'inden AYRI dosyada: `ActionDetailPage` zaten Sonar S3776
 * sınırında ve tablo dalı orada kalıyor; iki dalın yan yana durması sayfayı büyütürdü. Emsal:
 * `ActionNotesSection`.
 *
 * <p>Kolonlar karta şöyle iniyor: **Ad** başlık, **Durum** başlığın yanında rozet, **Kayıt No** alt
 * satır (yalnız yetki varsa — masaüstündeki `showRecordNumber` ile aynı koşul), **Atanan Kişi** ve
 * **Hedef Teslim Tarihi** bilgi satırı, **Sil** eylem alanı. Veri, işlemler ve koşullar masaüstüyle
 * birebir aynıdır; yalnız sunum değişir.
 *
 * <p><b>Kart tıklanabilir</b> (masaüstündeki `mtg-row-clickable` satır tıklamasının karşılığı) ve biçim
 * **overlay-link**'tir: kart kökü handler'sız düz bir `<div>`, tıklanabilirliği başlıktaki gerçek
 * `<button>` verir ve o kontrol kartı `::after` ile kaplar (`ui.css` → `.ui-card--linked`). Sil düğmesi
 * o kontrolün KARDEŞİdir, içinde değil — ARIA'da interaktif kontrol başka bir interaktif kontrol
 * içeremez (THY inceleme bulgusu, `ceo-frontend` PR, 15.09.2026; anayasa → kart kuralı).
 *
 * <p>Önceki biçim kart kökünde `role="button"` + `isCardBodyClick`/`e.target === e.currentTarget`
 * elemesi taşıyordu. O eleme **yalnız fare/klavye davranışını** düzeltiyordu, semantiği değil; artık
 * kontroller kardeş olduğu için elemeye hiç gerek yok. Emsal: `ActionViewCards` · `ActionEditorCards`,
 * bariyer: `cardNestedInteractive.test.tsx`.
 */
export default function SubActionCards({
  subActions,
  showRecordNumber,
  editable,
  onOpen,
  onDelete,
}: Readonly<{
  subActions: SubAction[]
  showRecordNumber: boolean
  editable: boolean
  onOpen: (id: number) => void
  onDelete: (id: number) => void
}>) {
  const { t } = useTranslation()
  return (
    <div className="ui-card-list act-subaction-cards">
      {subActions.map((s) => {
        const removeLabel = deleteLabel(t, 'actions.detail.subActions.delete', s.name)
        return (
        <div key={s.id} className="ui-card ui-card--linked">
          {/* Başlık `ui-card-head` İÇİNDE kalır (durum rozetiyle aynı satır, düzen korunur);
              `.ui-card-head` konumlanmamış olduğu için kaplama yine `.ui-card--linked` köküne
              göre çözülür ve TÜM kartı örter. */}
          <div className="ui-card-head">
            <button type="button" className="ui-card-link" onClick={() => onOpen(s.id)}>
              <span className="ui-card-title">{s.name}</span>
            </button>
            <ActionStatusTag statusId={s.statusId} name={s.statusName ?? ''} />
          </div>
          {showRecordNumber && s.recordNumber && (
            <span className="ui-card-sub">{s.recordNumber}</span>
          )}
          {/* Açıklama (2026-09-10) — kartta kendi satırında; masaüstündeki kolonun karşılığı. */}
          {s.description && <div className="ui-card-row act-card-desc">{s.description}</div>}
          <div className="ui-card-row">
            {s.assignee?.fullName && <span>{s.assignee.fullName}</span>}
            {s.deadline && <span>{formatDate(s.deadline)}</span>}
          </div>
          {/* Sil yalnız MANUEL alt görevde ve düzenlenebilir bağlamda — masaüstü kolonuyla aynı koşul. */}
          {editable && s.manual && (
            <div className="ui-card-actions">
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={removeLabel}
                title={removeLabel}
                onClick={() => onDelete(s.id)}
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        )
      })}
    </div>
  )
}
